import React, { useEffect, useState } from "react";
import { auth } from "../../firebase";
import { useAuth } from "../../AuthContext";

// Admin-only: problems customers reported from a bar's page in the app
// (closed, not a bar, wrong phone, ...), and every bar that's hidden.
// 3 "closed"/"not a bar" reports hide a bar automatically until you decide.

const URL = "https://us-central1-bar-buzz-a6645.cloudfunctions.net/barReports";

async function post(body) {
  const token = await auth.currentUser?.getIdToken();
  const res = await fetch(URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

const box = { background: "#17131F", borderRadius: "14px", padding: "18px", marginBottom: "14px" };
const btn = {
  minHeight: "40px", padding: "0 14px", borderRadius: "8px", border: "none",
  font: "700 14px Inter, sans-serif", cursor: "pointer", marginRight: "8px", marginTop: "8px",
};
const HIDDEN_LABEL = { closed: "Closed for good", notbar: "Not a bar", reported: "Hidden by 3+ customer reports" };

function ago(ms) {
  if (!ms) return "";
  const mins = Math.round((Date.now() - ms) / 60000);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs} hr ago`;
  return `${Math.round(hrs / 24)} days ago`;
}

export default function AdminReports() {
  const { role } = useAuth();
  const [groups, setGroups] = useState([]);
  const [hiddenBars, setHiddenBars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  // "Remove by name": find bars whose name has words like "nature center".
  const [words, setWords] = useState("nature center, wildlife, wild life");
  const [finding, setFinding] = useState(false);
  const [found, setFound] = useState(null); // null = not searched yet
  const [scanned, setScanned] = useState(0);
  const [picked, setPicked] = useState(() => new Set());
  const [removing, setRemoving] = useState(false);
  const [removedMsg, setRemovedMsg] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await post({ action: "list" });
      setGroups(data.groups || []);
      setHiddenBars(data.hiddenBars || []);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (role === "admin") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  if (role !== "admin") return <p style={{ color: "#fff" }}>Admins only.</p>;

  const findByName = async () => {
    const list = words.split(",").map((w) => w.trim()).filter((w) => w.length >= 3);
    if (!list.length) return;
    setFinding(true);
    setError("");
    setRemovedMsg("");
    setFound([]);
    setScanned(0);
    let after = "";
    let all = [];
    let count = 0;
    try {
      for (;;) {
        const r = await post({ action: "findByWords", words: list, after });
        all = all.concat(r.matches || []);
        count += r.scanned;
        setFound([...all]);
        setScanned(count);
        if (r.done || !r.next) break;
        after = r.next;
      }
      setPicked(new Set(all.filter((m) => !m.claimed).map((m) => m.barId)));
    } catch (e) {
      setError(`Search stopped: ${e.message}`);
    }
    setFinding(false);
  };

  const removePicked = async () => {
    const ids = [...picked];
    if (!ids.length || !window.confirm(`Remove ${ids.length} places from the app as "not a bar"?`)) return;
    setRemoving(true);
    setError("");
    let total = 0;
    try {
      for (let i = 0; i < ids.length; i += 400) {
        const r = await post({ action: "hideMany", barIds: ids.slice(i, i + 400) });
        total += r.hidden || 0;
      }
      setRemovedMsg(`✓ Removed ${total} places. They're in Hidden bars below if you need one back.`);
      setFound(null);
      setPicked(new Set());
      await load();
    } catch (e) {
      setError(`Couldn't remove them all: ${e.message}`);
    }
    setRemoving(false);
  };

  const act = async (barId, body, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(barId);
    setError("");
    try {
      await post({ barId, ...body });
      await load();
    } catch (e) {
      setError(e.message);
    }
    setBusy("");
  };

  return (
    <div style={{ color: "#fff", maxWidth: "960px" }}>
      <h2 style={{ marginBottom: "6px" }}>Reports</h2>
      <p style={{ color: "#B8B0C8", marginBottom: "16px", lineHeight: 1.5 }}>
        Problems customers reported with a bar's listing. <strong>Closed</strong> and <strong>Not a bar</strong>{" "}
        remove the bar from the app; <strong>Edit bar</strong> lets you fix its phone, address or website. Tip: to
        remove a bar yourself (like a nature center), open it in the app and use "Report a problem" while logged in
        as admin. It's hidden right away and shows up below.
      </p>
      {error && <div style={{ ...box, background: "#3A1420", color: "#FFC2D1" }}>{error}</div>}

      <div style={box}>
        <h3 style={{ margin: "0 0 6px" }}>Remove places by name</h3>
        <p style={{ color: "#B8B0C8", margin: "0 0 10px", lineHeight: 1.5 }}>
          Finds every place whose name contains one of these words (separate with commas). Check the list, untick
          any real bars, then remove the rest. Claimed bars are never removed.
        </p>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <input
            value={words}
            onChange={(e) => setWords(e.target.value)}
            style={{ flex: "1 1 320px", minHeight: "40px", padding: "0 10px", borderRadius: "8px", border: "1px solid #3A3350", background: "#0B0B12", color: "#fff", font: "500 15px Inter, sans-serif" }}
          />
          <button style={{ ...btn, marginTop: 0, background: "#8B5CF6", color: "#fff" }} onClick={findByName} disabled={finding || removing}>
            {finding ? `Searching… (${scanned.toLocaleString()} checked)` : "Find them"}
          </button>
        </div>
        {removedMsg && <p style={{ color: "#4ADE80", margin: "10px 0 0" }}>{removedMsg}</p>}
        {found && !finding && (
          <div style={{ marginTop: "12px" }}>
            <p style={{ color: "#B8B0C8", margin: "0 0 8px" }}>
              {found.length} found out of {scanned.toLocaleString()} places · {picked.size} ticked
            </p>
            <div style={{ maxHeight: "360px", overflowY: "auto", border: "1px solid #241E33", borderRadius: "10px" }}>
              {found.map((m) => (
                <label key={m.barId} style={{ display: "flex", gap: "10px", alignItems: "flex-start", padding: "8px 10px", borderBottom: "1px solid #241E33", cursor: m.claimed ? "default" : "pointer" }}>
                  <input
                    type="checkbox"
                    disabled={m.claimed}
                    checked={picked.has(m.barId)}
                    onChange={(e) =>
                      setPicked((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(m.barId);
                        else next.delete(m.barId);
                        return next;
                      })
                    }
                    style={{ marginTop: "4px" }}
                  />
                  <span>
                    <strong>{m.name}</strong>
                    {m.claimed && <span style={{ color: "#B9A6FF" }}> · has owner (kept)</span>}
                    <br />
                    <span style={{ color: "#B8B0C8", fontSize: "14px" }}>{m.address}</span>
                  </span>
                </label>
              ))}
            </div>
            {found.length > 0 && (
              <button style={{ ...btn, background: "#F87171", color: "#0B0B12" }} onClick={removePicked} disabled={removing || picked.size === 0}>
                {removing ? "Removing…" : `Remove ${picked.size} from the app`}
              </button>
            )}
          </div>
        )}
      </div>

      <h3 style={{ margin: "8px 0 10px" }}>
        Open reports {loading ? "" : `(${groups.length})`}
        <button style={{ ...btn, background: "#2A2238", color: "#fff", marginLeft: "12px" }} onClick={load} disabled={loading}>
          {loading ? "Loading…" : "Refresh"}
        </button>
      </h3>
      {!loading && groups.length === 0 && <div style={box}>No open reports. 🎉</div>}

      {groups.map((g) => {
        const counts = {};
        g.reports.forEach((r) => (counts[r.label] = (counts[r.label] || 0) + 1));
        return (
          <div key={g.barId} style={box}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: "18px", fontWeight: 800 }}>
                  {g.barName || "Bar"}
                  {g.claimed && <span style={{ color: "#B9A6FF", fontWeight: 500, fontSize: "14px" }}> · has owner</span>}
                  {g.hidden && <span style={{ color: "#F87171", fontWeight: 600, fontSize: "14px" }}> · hidden now</span>}
                </div>
                <div style={{ color: "#B8B0C8", fontSize: "14px" }}>{g.address}</div>
                <div style={{ color: "#B8B0C8", fontSize: "14px" }}>
                  {g.phone && <a href={`tel:${g.phone.replace(/[^0-9+]/g, "")}`} style={{ color: "#FF6FA3" }}>{g.phone}</a>}
                  {g.website && (
                    <a href={g.website} target="_blank" rel="noreferrer" style={{ color: "#B8B0C8", marginLeft: g.phone ? "10px" : 0 }}>
                      website
                    </a>
                  )}
                  {g.categories.length > 0 && <span style={{ marginLeft: "10px" }}>Listed as: {g.categories.join(", ")}</span>}
                </div>
              </div>
              <div style={{ color: "#8A84A0", fontSize: "13px" }}>{ago(g.latest)}</div>
            </div>

            <div style={{ margin: "10px 0 4px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {Object.entries(counts).map(([label, n]) => (
                <span key={label} style={{ padding: "4px 10px", borderRadius: "999px", background: "#2A1730", color: "#FF9CC1", fontSize: "14px", fontWeight: 700 }}>
                  {label} × {n}
                </span>
              ))}
            </div>
            {g.reports.filter((r) => r.note).map((r, i) => (
              <div key={i} style={{ color: "#D6D2E4", fontSize: "15px", margin: "6px 0", borderLeft: "3px solid #3A3350", paddingLeft: "10px" }}>
                "{r.note}" <span style={{ color: "#8A84A0", fontSize: "13px" }}>· {r.label}{r.byAdmin ? " · you" : ""}</span>
              </div>
            ))}

            <div>
              <button
                style={{ ...btn, background: "#F87171", color: "#0B0B12" }}
                disabled={busy === g.barId}
                onClick={() => act(g.barId, { action: "resolve", decision: "closed" }, `Remove ${g.barName} from the app as closed?`)}
              >
                Closed: remove
              </button>
              <button
                style={{ ...btn, background: "#FACC15", color: "#0B0B12" }}
                disabled={busy === g.barId}
                onClick={() => act(g.barId, { action: "resolve", decision: "notbar" }, `Remove ${g.barName} from the app (not a bar)?`)}
              >
                Not a bar: remove
              </button>
              <a href={`/admin/add-bar?bar=${encodeURIComponent(g.barId)}`} style={{ ...btn, display: "inline-flex", alignItems: "center", background: "#8B5CF6", color: "#fff", textDecoration: "none" }}>
                Edit bar
              </a>
              <button style={{ ...btn, background: "#2A2238", color: "#4ADE80" }} disabled={busy === g.barId} onClick={() => act(g.barId, { action: "resolve", decision: "fixed" })}>
                Fixed it
              </button>
              <button style={{ ...btn, background: "#2A2238", color: "#B8B0C8" }} disabled={busy === g.barId} onClick={() => act(g.barId, { action: "resolve", decision: "dismiss" })}>
                Dismiss (report was wrong)
              </button>
            </div>
          </div>
        );
      })}

      <h3 style={{ margin: "26px 0 10px" }}>Hidden bars {loading ? "" : `(${hiddenBars.length})`}</h3>
      <p style={{ color: "#B8B0C8", marginBottom: "10px" }}>Removed from the app. Put one back if it was a mistake.</p>
      {!loading && hiddenBars.length === 0 && <div style={box}>None.</div>}
      {hiddenBars.map((b) => (
        <div key={b.barId} style={{ ...box, display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
          <div>
            <div style={{ fontWeight: 700 }}>{b.barName || "Bar"}</div>
            <div style={{ color: "#B8B0C8", fontSize: "14px" }}>
              {b.address} · {HIDDEN_LABEL[b.hiddenReason] || b.hiddenReason} {b.hiddenAt ? `· ${ago(b.hiddenAt)}` : ""}
            </div>
          </div>
          <button style={{ ...btn, background: "#2A2238", color: "#fff", marginTop: 0 }} disabled={busy === b.barId} onClick={() => act(b.barId, { action: "unhide" }, `Put ${b.barName} back in the app?`)}>
            Put back in the app
          </button>
        </div>
      ))}
    </div>
  );
}
